import React, { useState, useRef, useLayoutEffect, useEffect } from 'react';
import { useSelector } from 'react-redux';
import animateScrollTo from 'animated-scroll-to';
import { getNavbarHeight, getViewportDimensions, keepSpaceTogether } from '../../../../models/Utils';
import { useMediaQuery } from 'react-responsive';

import './MosaicSection.scss';

import LearnMoreLink from '../../../Common/LearnMoreLink/LearnMoreLink';
import Arrow from '../../../Common/Arrow/Arrow';
import MosaicColumn from '../MosaicColumn/MosaicColumn';
import { ADAARIARoles } from '../../../../models/ADA';

const togetherString = 'V Team';

const MosaicSection = React.forwardRef(({ header, body, columns, ctaData, isActive }, theRef) => {
  const container = useRef(null);
  const isPortrait = useMediaQuery({ query: '(orientation: portrait)' });
  const [ containerWidth, setContainerWidth] = useState(0);
  const refContainerWidth = useRef(containerWidth);
  const [ mosaicHeight, setMosaicHeight ] = useState(0);
  const [ contentDimensions, setContentDimensions ] = useState({
    width: 0,
    paddingLeft: 0,
    paddingRight: 0
  });
  const refContentDimensions = useRef(contentDimensions);
  const introElement = useRef(null);
  const scroller = useRef(null);
  const bottomSpacer = useRef(null);
  const [ mosaicUnitWidth, setMosaicUnitWidth] = useState(0);
  const isSnappedConclusion = useRef(false);
  const refMosaicUnitWidth = useRef(mosaicUnitWidth);
  const lastScrollerPos = useRef(0);
  const introWrapper = useRef(null);
  const introBodyText = useRef(null);
  const introTextOffset = useRef(0);
  const introTextWentUp = useRef(false);
  const wiggleShown = useRef(false);
  const wiggleEnded = useRef(false);
  
  const menuOpened = useSelector(state => state.data.menuOpened);

  useEffect(() => {
    setTimeout(() => {
      if(isActive){
        setIntroTextPosition('up');
        performAutoScroll('right');
      } else {
        setIntroTextPosition('down');
      }
    }, 500);
  }, [isActive])

  const getColumns = (unitWidth) => {
    let columnElements = [];

    columns.forEach((columnData, i) => {
      columnElements.push(
        <MosaicColumn
          key={`column-${i}`}
          ref={{
            scroller,
            wiggleEnded
          }}
          unitWidth={unitWidth}
          columnType={columnData.ColumnType}
          data={
            columnData.ColumnType === '1'
            ?
            columnData.HalfColumn
            :
            columnData.Column
          }
        />
      );
    });

    return columnElements;
  };

  const isAutoScrolling = useRef(false);

  const performAutoScroll = (dir) => {
    if (isAutoScrolling.current) return;

    setIntroTextPosition('up');
    isAutoScrolling.current = true;
    isDragged.current = false;
    const viewport = getViewportDimensions();
    const bodyTextLeft = introBodyText.current.getBoundingClientRect().x;
    const isOnDesktop = viewport.width / 2 <= bodyTextLeft;
    const epsilon = refMosaicUnitWidth.current / 2;
    const ctaLeft = refContentDimensions.current.paddingLeft + refContentDimensions.current.width - refContainerWidth.current;
    const currentScroll = scroller.current.scrollLeft;
    const hiddenOnTheRight = refContentDimensions.current.paddingLeft + refContentDimensions.current.width - refContainerWidth.current - refContentDimensions.current.paddingRight - (currentScroll + viewport.width);
    const moveDiff = dir === 'right' && hiddenOnTheRight > 0 ? Math.min(viewport.width / 2, hiddenOnTheRight + epsilon - 2) : viewport.width / 2;
    let targetScroll = currentScroll + (dir === 'right' ? 1 : -1) * moveDiff;
    if (dir === 'right' && targetScroll + refContentDimensions.current.paddingRight + refContainerWidth.current > ctaLeft - refContentDimensions.current.paddingRight + epsilon) {
      targetScroll = ctaLeft - refContentDimensions.current.paddingRight;
    } else if (dir === 'left' && targetScroll - epsilon < ctaLeft - refContentDimensions.current.paddingRight && targetScroll + refContentDimensions.current.paddingRight + refContainerWidth.current > ctaLeft) {
      targetScroll = Math.max(0, ctaLeft - refContentDimensions.current.paddingRight - refContainerWidth.current - refContentDimensions.current.paddingRight);
    }
    animateScrollTo([targetScroll, null], {
      elementToScroll: scroller.current,
      cancelOnUserAction: false
    })
    .then(() => {
      setTimeout(() => {
        isAutoScrolling.current = false;
      }, 100);
    });
  };

  const handleScroll = () => {
    if (isAutoScrolling.current) {
      lastScrollerPos.current = scroller.current.scrollLeft;
    } else {
      const epsilon = refMosaicUnitWidth.current / 2;
      const ctaLeft = refContentDimensions.current.paddingLeft + refContentDimensions.current.width - refContainerWidth.current;
      const currentScroll = scroller.current.scrollLeft;
 
      var snapTarget = refContentDimensions.current.width - refMosaicUnitWidth.current;

      if(currentScroll > snapTarget && !isSnappedConclusion.current){
       // performAutoScroll('right');
        isSnappedConclusion.current = true;
      }else if (currentScroll < snapTarget && isSnappedConclusion.current){
      //  performAutoScroll('left');
        isSnappedConclusion.current = false;
      }
    }
    
    handleIntroTextPositioning();
  };

  const handleBodyScroll = () => {
    
    if (!wiggleShown.current) {
      const viewport = getViewportDimensions();
      const bodyTextLeft = introBodyText.current.getBoundingClientRect().x;
      const isOnDesktop = viewport.width / 2 <= bodyTextLeft;
      const scrollerRect = scroller.current.getBoundingClientRect();
      if (scroller.current.scrollLeft === 0 && scrollerRect.y + scrollerRect.height <= viewport.height) {
        wiggleShown.current = true;
        const animatorProperties = {
          elementToScroll: scroller.current,
          cancelOnUserAction: false,
          speed: isOnDesktop ? 1000 : 2000,
          easing: (x) => {
            const c1 = 1.70158;
            const c3 = c1 + 1;

            return 1 + c3 * Math.pow(x - 1, 3) + c1 * Math.pow(x - 1, 2);
          }
        };
        setIntroTextPosition('up');
        const finalScrollerOffset = isOnDesktop
          ? (refContentDimensions.current.paddingLeft - bodyTextLeft)
          : (refContentDimensions.current.paddingLeft - Math.round(viewport.width * 1 / 3));
        setTimeout(() => {
          isAutoScrolling.current = true;
          animateScrollTo([finalScrollerOffset, null], animatorProperties)
          .then(() => {
            isAutoScrolling.current = false;
            setTimeout(() => {
              wiggleEnded.current = true;
            }, 100);
          });
        }, 500);
      }
    }
  };

  const handleIntroTextPositioning = () => {

    if (introTextWentUp.current) return;

    if (wiggleShown.current && wiggleEnded.current ){//&& scroller.current.scrollLeft > 0) {
      setIntroTextPosition('up');   
    } else {
      setIntroTextPosition('down');    
    }
  };

  const setIntroTextPosition = (position) => {
    if (position === 'up' || isPortrait) {
      introWrapper.current.style.top = '0px';
      introWrapper.current.classList.remove('down');
      introWrapper.current.classList.add('up');
      introTextWentUp.current = true;
    } else {
      introWrapper.current.style.top = `${introTextOffset.current}px`;
      introWrapper.current.classList.remove('up');
      introWrapper.current.classList.add('down');
    }
  };

  const handleResize = () => {
    if (!container || !introElement) return;

    
    const viewport = getViewportDimensions();
    const containerComputedStyle = window.getComputedStyle(container.current, null);
    const introElementHeight = introElement && introElement.current && introElement.current.getBoundingClientRect().height;
    const containerWidth = container && container.current && container.current.getBoundingClientRect().width - parseInt(containerComputedStyle.getPropertyValue('padding-left'), 10) - parseInt(containerComputedStyle.getPropertyValue('padding-right'), 10);
    const mosaicHeight = Math.max(viewport.height - introElementHeight - getNavbarHeight() - bottomSpacer.current.getBoundingClientRect().height, 400);
    const unitWidth = Math.round(mosaicHeight / 3);
    let contentInnerWidth = 0;
    columns.forEach((column) => {
      contentInnerWidth += column.ColumnType === '1' ? unitWidth : 2 * unitWidth;
    });
    const paddingRight = viewport.width / 2 - containerWidth / 2;
    const contentDimensions = {
      width: contentInnerWidth + containerWidth + paddingRight,
      paddingLeft: viewport.width - paddingRight / 2,
      paddingRight: paddingRight
    };
    scroller.current.scrollTo(0, 0);
    lastScrollerPos.current = 0;

    setContainerWidth(containerWidth);
    refContainerWidth.current = containerWidth;
    setMosaicHeight(mosaicHeight);
    setMosaicUnitWidth(unitWidth);
    refMosaicUnitWidth.current = unitWidth;
    setContentDimensions(contentDimensions);
    refContentDimensions.current = contentDimensions;
    introTextOffset.current = introElementHeight / 2 + mosaicHeight / 2;
    handleIntroTextPositioning();
  };

  const isDragged = useRef(false);
  const mouseStartX = useRef(null);
  const mouseScrollerStart = useRef(null);

  const handleMouseDown = (ev) => {
    isDragged.current = true;
    mouseStartX.current = ev.clientX;
    mouseScrollerStart.current = scroller.current.scrollLeft;
  };

  const handleMouseUp = () => {
    
    isDragged.current = false;
    mouseStartX.current = null;
    mouseScrollerStart.current = null;
    setIntroTextPosition('up');
  };

  const handleMouseMove = (ev) => {
    if (isDragged.current) {
      scroller.current.scrollTo(mouseScrollerStart.current - (ev.clientX - mouseStartX.current), 0);
    } else {
      handleMouseUp();
    }
  };

  const handleKeyDown = (e) => {
    setIntroTextPosition('up');
  }

  useLayoutEffect(() => {
    handleResize();

    const currentScroller = scroller.current;

    window.addEventListener('resize', handleResize);
    document.body.addEventListener('scroll', handleBodyScroll);
    currentScroller.addEventListener('scroll', handleScroll);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('resize', handleResize);
      document.body.removeEventListener('scroll', handleBodyScroll);
      currentScroller.removeEventListener('scroll', handleScroll);
      document.removeEventListener('keydown', handleKeyDown);
  };
  }, []);

  useEffect(() => {
    handleResize();
    wiggleShown.current = false;
    wiggleEnded.current = false;
    introTextWentUp.current = false;
    handleIntroTextPositioning();
  }, [ columns ]);

  return (
    <div className="bbf-mosaic-section" ref={theRef}>
      <div className="bbf-ms-intro" ref={introWrapper}>
        <div className="cpvi-grid-container" ref={container}>
          <div className="cpvi-grid-row" ref={introElement}>
            <div className="cpvi-grid-col w6 bbf-ms-intro-column bbf-ms-intro-title-container" role={ADAARIARoles.Heading}>{keepSpaceTogether(header, togetherString)}</div>
            <div className="cpvi-grid-col w6 bbf-ms-intro-column bbf-ms-intro-text-container">
              <div className="cont-75" ref={introBodyText} >{keepSpaceTogether(body, togetherString)}</div>
            </div>
          </div>
        </div>
      </div>
      <div className="bbf-ms-mosaic" style={{ height: `${mosaicHeight}px` }}>
        <div
          className="bbf-ms-scrollable"
          ref={scroller}
          onMouseDown={handleMouseDown}
          onMouseUp={handleMouseUp}
          onMouseMove={handleMouseMove}
        >
          {
            (() => { 
              if (scroller && scroller.current) {
                scroller.current.scrollTo(0, 0);
                wiggleShown.current = false;
              }
              return null;
            })() 
          }
          <div
            className="bbf-ms-scrollable-content"
            style={{
              width: `${contentDimensions.width}px`,
              paddingLeft: `${contentDimensions.paddingLeft}px`,
              paddingRight: `${contentDimensions.paddingRight}px`
            }}
          >
            { getColumns(mosaicUnitWidth) }
            <div className="bbf-ms-cta-content" style={{ width: `${containerWidth}px`, paddingLeft: `${contentDimensions.paddingRight}px` }}>
              <div className="bbf-ms-cta-wrapper">
                <div className="bbf-ms-cta-title">{ctaData.Title}</div>
                <div className="bbf-ms-cta-text">{ctaData.Body}</div>
                <LearnMoreLink
                  text={ctaData.LearnMoreText}
                  to={ctaData.Link}
                  external={true}
                />
              </div>
            </div>
          </div>
        </div>
        <div className="bbf-ms-nav" style={{ width: `${containerWidth}px`, marginLeft: `-${containerWidth / 2}px`}}>
          <div className="bbf-ms-nav-arrow left">
            <Arrow direction="left" darkMode={true} width="10px" handleClick={ () => performAutoScroll('left') } altText="Scroll Left"/>
          </div>
          <div className="bbf-ms-nav-arrow right">
            <Arrow direction="right" darkMode={true} width="10px" handleClick={ () => performAutoScroll('right') } altText="Scroll Right"/>
          </div>
        </div>
      </div>
      <div className="bbf-ms-bottom-spacer" ref={bottomSpacer}></div>
    </div>
  );
});

export default MosaicSection;
